import { useMemo, useState } from 'react'

import { Alert, Fade, Icon, Snackbar } from '@mui/material'
import Card from '@mui/material/Card'
import { useQuery } from '@tanstack/react-query'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { useMaterialUIController } from 'context'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import DataTable from 'modules/Tables/DataTable'

import PayphoneModal from './components/PayphoneModal'
import { PayphoneLocation } from './types'
import {
	createPayphoneLocation,
	deletePayphoneLocation,
	fetchPayphoneLocations,
	toPayphoneTable,
	updatePayphoneLocation,
} from './utils'

function PayphoneLocations(): JSX.Element {
	const [controller] = useMaterialUIController()
	const { session } = controller
	const orgId = session.data.organisation.id
	const defaultLat = session.data.organisation.default_location.geometry.lat
	const defaultLng = session.data.organisation.default_location.geometry.lng

	const [modalOpen, setModalOpen] = useState(false)
	const [selected, setSelected] = useState<PayphoneLocation | null>(null)
	const [notification, setNotification] = useState<{
		status: 'success' | 'error' | null
		message?: string
	}>({ status: null })

	const { data, error, isLoading, refetch } = useQuery({
		queryKey: ['payphone-locations', orgId],
		queryFn: () => fetchPayphoneLocations(orgId),
	})

	const locationsById = useMemo(() => {
		const map = new Map<string, PayphoneLocation>()
		;(data || []).forEach(location => {
			if (location.id) {
				map.set(location.id, location)
			}
		})
		return map
	}, [data])

	const tableData = useMemo(() => toPayphoneTable(data || []), [data])

	const openCreateModal = () => {
		setSelected(null)
		setModalOpen(true)
	}

	const openEditModal = (row: { [key: string]: any }) => {
		const location = locationsById.get(row.route)
		if (!location) return
		setSelected(location)
		setModalOpen(true)
	}

	const handleSave = async (location: PayphoneLocation) => {
		try {
			if (location.id) {
				await updatePayphoneLocation(orgId, location)
			} else {
				await createPayphoneLocation(orgId, location)
			}
			await refetch()
			setNotification({ status: 'success', message: 'Saved successfully' })
		} catch (err) {
			console.error(err)
			setNotification({ status: 'error', message: 'Failed to save payphone location' })
			throw err
		}
	}

	const handleDelete = async (id: string) => {
		try {
			await deletePayphoneLocation(orgId, id)
			await refetch()
			setNotification({ status: 'success', message: 'Deleted successfully' })
		} catch (err) {
			console.error(err)
			setNotification({ status: 'error', message: 'Failed to delete payphone location' })
			throw err
		}
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Snackbar
				open={notification.status !== null}
				autoHideDuration={4000}
				onClose={() => setNotification({ status: null })}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				TransitionComponent={Fade}
			>
				{notification.status && (
					<Alert severity={notification.status} variant="standard">
						{notification.message}
					</Alert>
				)}
			</Snackbar>
			<MDBox pt={6} pb={3}>
				<Card>
					<MDBox p={3} mb={1} lineHeight={1}>
						<MDTypography variant="h2" fontWeight="medium">
							Payphone Locations
						</MDTypography>
						<MDTypography variant="button" color="text">
							Manage payphone locations and associated phone numbers for pickups.
						</MDTypography>
					</MDBox>
					<MDBox mx={3} display="flex" justifyContent="space-between" mb={2}>
						<MDButton color="primary" variant="outlined" onClick={openCreateModal}>
							ADD NEW
							<Icon sx={{ ml: 1 }}>add_circle_outlined</Icon>
						</MDButton>
					</MDBox>
					{isLoading ? (
						<LoadingComponent type="box" height={600} containerStyle={{ padding: 50 }} />
					) : error ? (
						<MDBox p={3}>
							<MDTypography variant="button" color="error">
								Failed to load payphone locations:{' '}
								{String((error as any)?.message || error)}
							</MDTypography>
						</MDBox>
					) : (
						<DataTable table={tableData.table} canSearch onRowClick={openEditModal} />
					)}
				</Card>
			</MDBox>
			<PayphoneModal
				open={modalOpen}
				initialValue={selected}
				defaultLat={defaultLat}
				defaultLng={defaultLng}
				onClose={() => setModalOpen(false)}
				onSave={handleSave}
				onDelete={handleDelete}
			/>
		</DashboardLayout>
	)
}

export default PayphoneLocations
